import { Role } from '../generated/graphql';

/**
 * Route access utilities for role-based redirects
 * Maps routes to required roles based on permission-matrix.md
 */

const hasAnyRole = (roles: Role[], required: Role[]): boolean =>
  required.some(r => roles.includes(r));

const PARTNER_ENQUIRIES = '/crm/partner-enquiries';

/**
 * Roles that open no care-portal page: the non-staff roles, and growth, which works through its
 * MCP door only (YOJO-3614 [growth staff role + growth MCP door]). Any other role (including a
 * future one) opens pages.
 */
const NO_PORTAL_PAGE_ROLES: readonly string[] = [Role.Participant, Role.User, Role.Growth];

/** A growth-only user's one page: their own profile (YOJO-3614). */
const OWN_PROFILE = '/profile/info';

/**
 * A sales user with no other role that opens pages. Sales works CRM → Partner Enquiries and
 * nothing else (YOJO-3371 [care-portal CRM page for partner enquiries]).
 */
export function isSalesOnly(roles: Role[]): boolean {
  return roles.includes(Role.Sales) && roles.every(r => r === Role.Sales || NO_PORTAL_PAGE_ROLES.includes(r));
}

/**
 * A growth user with no other role that opens pages. Growth (marketing, website, prospects, Loops)
 * works through MCP and sees no member or customer data in the portal: only their own profile
 * (YOJO-3614 [growth staff role + growth MCP door]).
 */
export function isGrowthOnly(roles: Role[]): boolean {
  return roles.includes(Role.Growth) && roles.every(r => NO_PORTAL_PAGE_ROLES.includes(r));
}

const isPathOrBelow = (path: string, base: string): boolean =>
  path === base || path.startsWith(`${base}/`);

/**
 * Check if a user with given roles can access a specific route
 */
export function canAccessRoute(path: string, roles: Role[]): boolean {
  // A growth-only user may open their own profile and nothing else (YOJO-3614)
  if (isGrowthOnly(roles)) {
    return isPathOrBelow(path, '/profile');
  }

  // Dashboard - not for a sales-only user (no Dashboard in their sidebar; the /dashboard route
  // sends them to Partner Enquiries), so Login falls back to their default instead
  if (isPathOrBelow(path, '/dashboard') && isSalesOnly(roles)) {
    return false;
  }

  // CRM - a sales-only user may open /crm (it sends them on) and Partner Enquiries, no other CRM
  // page (YOJO-3371). Every other role is unchanged here.
  if (isSalesOnly(roles) && path.startsWith('/crm/') && !isPathOrBelow(path, PARTNER_ENQUIRIES)) {
    return false;
  }

  // Dashboard tabs
  if (path.startsWith('/dashboard/chat')) {
    return hasAnyRole(roles, [Role.Admin, Role.Nurse]);
  }
  if (path.startsWith('/dashboard/membership')) {
    return hasAnyRole(roles, [Role.Admin, Role.Developer]);
  }

  // Settings - admin only
  if (path.startsWith('/settings')) {
    return roles.includes(Role.Admin);
  }

  // Programs - admin or content
  if (path.startsWith('/programs')) {
    return hasAnyRole(roles, [Role.Admin, Role.Content]);
  }

  // Tools notifications - admin or developer
  if (path.startsWith('/tools/notifications')) {
    return hasAnyRole(roles, [Role.Admin, Role.Developer]);
  }

  // Users section - admin, nurse, developer only (NOT Content)
  if (path.startsWith('/users')) {
    return hasAnyRole(roles, [Role.Admin, Role.Nurse, Role.Developer]);
  }

  // Wellbeing pages - admin or nurse only
  if (path.includes('/wellbeing')) {
    return hasAnyRole(roles, [Role.Admin, Role.Nurse]);
  }

  // All other routes accessible to authenticated users
  return true;
}

/**
 * Get the default landing page for a user based on their roles
 */
export function getDefaultRouteForRole(roles: Role[]): string {
  // Growth-only users have their own profile and nothing else
  if (isGrowthOnly(roles)) {
    return OWN_PROFILE;
  }

  // Sales-only users have exactly one page
  if (isSalesOnly(roles)) {
    return PARTNER_ENQUIRIES;
  }

  // Admin, Nurse default to recent feedback
  if (hasAnyRole(roles, [Role.Admin, Role.Nurse])) {
    return '/dashboard/feedback';
  }

  // Content and Analyst users default to recent activity
  if (hasAnyRole(roles, [Role.Content, Role.Analyst])) {
    return '/dashboard/activity';
  }

  // Safe fallback
  return '/dashboard/activity';
}

/**
 * Where a route guard sends a user it refuses (the guard still shows its permission toast):
 * a sales-only user to Partner Enquiries, their only page; a growth-only user to their own profile
 * (YOJO-3614); everyone else to /dashboard.
 */
export function refusalRedirectFor(roles: Role[]): string {
  if (isGrowthOnly(roles)) return OWN_PROFILE;
  return isSalesOnly(roles) ? PARTNER_ENQUIRIES : '/dashboard';
}
